import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import _ from "lodash";
import { roundMoney } from "../../utils/money";

export const LEDGER_PDF_OPTIONS = {
  orientation: "p",
  unit: "pt",
  format: "a4",  // Standard A4: 595pt × 842pt
  putOnlyUsedFonts: true,
};

export const ledgerTemplate = (data, ctx = {}) => {
  console.log("ledgerTemplate>>>>>>>", data)
  let {
    ledgers,
    startDate,
    endDate,
    clientName,
    clientAddress,
    closingBalance,
    ledgerType,
    openingBalance,
    pay,
  } = data;
  console.log("ledgerType>>>>>>>", data.startDate, data.endDate)

  const safeClientAddress =
    clientAddress != null ? String(clientAddress).trim() : "";

  // Use company's opening balance as-is: 0 stays 0, otherwise the stored value. Ledger rows print on top.
  let effectiveOpeningBalance = openingBalance != null ? Number(openingBalance) : 0;
  const safeLedgers = Array.isArray(ledgers) ? ledgers : [];
  const showTaxCols = safeLedgers.some((l) => Number.isFinite(Number(l?.gstAmount)));

  let tableData = [];
  let balance = effectiveOpeningBalance;
  let totalCredit = 0;
  let totalDebit = 0;
  let totalGst = 0;

  safeLedgers.map((o, index) => {
    let { codes, amount, type, date, code, paymentFor, paymentNote } = o;
    const numAmount = Number(amount) || 0;
    const gstAmt = Number(o?.gstAmount);
    const taxableVal = Number(o?.taxableValue);
    const hasTaxBreakup = Number.isFinite(gstAmt) && Number.isFinite(taxableVal);
    // For bills enriched with tax: treat ledger amount as final payable (tax-inclusive)
    const effectiveAmount = hasTaxBreakup ? taxableVal + gstAmt : numAmount;
    if (Number.isFinite(gstAmt)) totalGst += gstAmt;
    const isSale = paymentFor?.toLowerCase().includes("sale");
    const isPurchase = paymentFor?.toLowerCase().includes("purchase");

    if (type === "credit") balance -= effectiveAmount;
    if (type === "debit") balance += effectiveAmount;
    if (isSale) balance -= effectiveAmount;
    else if (isPurchase) balance += effectiveAmount;

    if (pay) {
      // Payment-mode credit/debit classification based on paymentFor
      if (isSale) totalCredit += effectiveAmount;
      if (isPurchase) totalDebit += effectiveAmount;

      let row = [
        index + 1,
        moment(date).format("DD-MM-YYYY"),
        code,
        isSale ? roundMoney(effectiveAmount) : "",
        isPurchase ? roundMoney(effectiveAmount) : "",
        roundMoney(balance),
      ];
      tableData.push(row);
    } else {
      // Standard ledger: credit/debit based purely on type
      if (type === "credit") totalCredit += effectiveAmount;
      if (type === "debit") totalDebit += effectiveAmount;

      const displayCode = paymentNote ? codes[0] + " -  " + paymentNote : codes[0];
      let row = showTaxCols
        ? [
            index + 1,
            moment(date).format("DD-MM-YYYY"),
            displayCode,
            type === "credit" ? roundMoney(effectiveAmount) : "",
            type === "debit" ? roundMoney(effectiveAmount) : "",
            hasTaxBreakup ? roundMoney(taxableVal) : "",
            hasTaxBreakup ? roundMoney(gstAmt) : "",
            roundMoney(balance),
          ]
        : [
            index + 1,
            moment(date).format("DD-MM-YYYY"),
            displayCode,
            type === "credit" ? roundMoney(effectiveAmount) : "",
            type === "debit" ? roundMoney(effectiveAmount) : "",
            roundMoney(balance),
          ];
      tableData.push(row);
    }
  });
  // }


  const doc = ctx?.doc || new jsPDF(LEDGER_PDF_OPTIONS);
  const pageWidth = doc.internal.pageSize.getWidth();
  const sectionStartPage = ctx?.sectionStartPage ?? doc.internal.getNumberOfPages?.() ?? 1;

  // Always show opening balance row (0 or company's stored value)
  const openingRow = showTaxCols
    ? ["", "", "Opening Balance", "", "", "", "", `${roundMoney(effectiveOpeningBalance)}`]
    : ["", "", "Opening Balance", "", "", `${roundMoney(effectiveOpeningBalance)}`];

  // Determine closing balance value (fallback to provided closingBalance or openingBalance)
  const closingBalanceValue =
    tableData.length > 0
      ? tableData[tableData.length - 1][5]
      : closingBalance ?? effectiveOpeningBalance;

  // Summary rows:
  // 1) Total row shows Credit/Debit totals only
  // 2) Closing Balance row shows Balance only
  const totalRow = [
    "",
    "",
    "Total",
    roundMoney(totalCredit),
    roundMoney(totalDebit),
    ...(showTaxCols ? ["", roundMoney(totalGst)] : []),
    "",
  ];

  const closingRow = [
    "",
    "",
    "Closing Balance",
    "",
    "",
    ...(showTaxCols ? ["", ""] : []),
    `${closingBalanceValue}`,
  ];

  doc.autoTable({
    startY: 70,
    // Reserve space for the header on every page to avoid overlap on page breaks.
    margin: { top: 60 },
    head: [
      showTaxCols
        ? ["Sr No.", "Date", "Codes", "Credit", "Debit", "Taxable", "GST", "Balance"]
        : ["Sr No.", "Date", "Codes", "Credit", "Debit", "Balance"],
    ],
    body: [openingRow, ...tableData, totalRow, closingRow],
    styles: { fontSize: 12, cellPadding: 5 },
    theme: "striped", // Set the theme to striped
    alternateRowStyles: { fillColor: [240, 240, 240] }, // Light grey for alternating rows
    tableLineColor: [0, 0, 0], // Black borders
    tableLineWidth: 0.1, // Table border width
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    }, // White header with black text
    didDrawPage: function (data) {
      // When appending multiple ledgers into one PDF, we need to draw the header
      // for the first page of each "section" (each company ledger).
      let currentPage = 1;
      try {
        currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      } catch (_) {}

      const isSectionFirstPage = currentPage === sectionStartPage;

      if (isSectionFirstPage) {
        doc.setFontSize(14);
        doc.text(`LEDGER`, pageWidth / 2, 30, { align: "center" });
        if (startDate != null && endDate != null) {
          const dateRangeText = `From: ${moment(startDate).format(
            "DD-MM-YYYY"
          )} To: ${moment(endDate).format("DD-MM-YYYY")}`;
          doc.setFontSize(12);
          doc.text(dateRangeText, pageWidth - 40, 30, { align: "right" });
          doc.setFontSize(14);
        }
        doc.text(`M/S: ${clientName}`, 40, 30);
        if (safeClientAddress) {
          doc.setFontSize(10);
          const addressLines = doc.splitTextToSize(safeClientAddress, 400);
          let y = 45;
          addressLines.forEach((line) => {
            doc.text(line, 40, y);
            y += 10;
          });
          doc.setFontSize(14);
        }
      } else {
        // Keep minimal header for continued pages of the same company.
        doc.setFontSize(12);
        doc.text(`M/S: ${clientName}`, 40, 30);
        if (startDate != null && endDate != null) {
          const dateRangeText = `From: ${moment(startDate).format(
            "DD-MM-YYYY"
          )} To: ${moment(endDate).format("DD-MM-YYYY")}`;
          doc.text(dateRangeText, pageWidth - 40, 30, { align: "right" });
        }
        doc.setFontSize(14);
      }

      // In bulk ledger PDFs we append multiple company ledgers into a single jsPDF.
      // In that case, we want footer page numbering to restart from 1 at the start of each company section.
      const hasBulkSectionStart = Number.isFinite(ctx?.sectionStartPage);
      const footerPageNumber = hasBulkSectionStart
        ? currentPage - sectionStartPage + 1
        : currentPage;

      let footerStr = `Page ${footerPageNumber}`;
      doc.text(
        footerStr,
        data.settings.margin.left,
        doc.internal.pageSize.height - 10
      );
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
    },
    bodyStyles: { textColor: [0, 0, 0] },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    columnStyles: {
      0: { lineWidth: 0.1, lineColor: [0, 0, 0] },
      1: { lineWidth: 0.1, lineColor: [0, 0, 0] },
      2: { lineWidth: 0.1, lineColor: [0, 0, 0] },
      3: { lineWidth: 0.1, lineColor: [0, 0, 0] },
      4: { lineWidth: 0.1, lineColor: [0, 0, 0] },
      5: { lineWidth: 0.1, lineColor: [0, 0, 0] },
    },
  });


  if (ctx?.doc) return doc;
  const pdfBlob = doc.output("blob");
  return pdfBlob;
};
